"use client";

import { useState } from "react";
import clsx from "clsx";
import { useTranslations } from "next-intl";

import IconChevronLeft from "@ui/central-icons/icons/IconChevronLeft";
import IconChevronRight from "@ui/central-icons/icons/IconChevronRight";
import { iconStrokeFix } from "@ui/central-icons/iconUtils";
import { LinkTracked } from "@ui/components/core/LinkTracked";
import { SignUpFeatureList } from "@ui/components/core/SignUpPopover";
import { EventName } from "@mw/shared/analyticsEventNames";

import { publicUrls } from "@/constants";
import type { FlowTemplateExample, FlowTemplateSchema } from "@/app/_api/flowTemplatesServer";
import { BlockErrorBoundary } from "@/app/_blocks/BlockErrorBoundary";
import { useFlowTemplateRun } from "@/app/_blocks/BlockFlowTemplate/useFlowTemplateRun";
import { FlowTemplateFormCard } from "@/app/_views/CreativeTemplate/HeroOmnibox/FlowTemplateFormCard";
import { FlowTemplateResultImage } from "@/app/_views/CreativeTemplate/HeroOmnibox/FlowTemplateResultImage";
import {
  FlowTemplateIdlePlaceholder,
  FlowTemplateRunningPlaceholder,
} from "@/app/_views/CreativeTemplate/HeroOmnibox/FlowTemplateResultPlaceholder";
import {
  isProcessingStep,
  isRateLimitedStep,
  type WorkflowResultType,
} from "@/app/_views/CreativeTemplate/HeroOmnibox/types";
import { useSignUpFeatureItems } from "@/app/_views/CreativeTemplate/HeroOmnibox/useSignUpFeatureItems";

export interface FlowTemplateRunPanelProps {
  flowTemplate: FlowTemplateSchema;
  flowTemplateExample: FlowTemplateExample | null;
  resultType: WorkflowResultType;
}

// Distinguishes the gallery's runs from the standalone block in PostHog (both
// reuse the omnibox event names + `useFlowTemplateRun`); see the `source`-tagging
// rationale in `useFlowTemplateRun`.
const ANALYTICS_SOURCE = "flow_template_gallery";

// Subtle text button shared by the mobile step controls ("back" / "view result").
const STEP_NAV_CLASS =
  "tw-flex tw-items-center tw-gap-1 tw-type-sm tw-text-gray-500 tw-outline-none tw-transition-colors hover:tw-text-black focus-visible:tw-outline-focus lg:tw-hidden";

/**
 * The run experience shown inside the gallery's modal. Unlike the standalone
 * `BlockFlowTemplate` (single column + a SignUpPopover), this lays out:
 *  - desktop (lg+): two columns — the run form on the left, a preview pane on
 *    the right that becomes the result + an inline signup CTA once generated;
 *  - mobile: a single-column stepped flow — the form first, then (once a result
 *    lands) the result + CTA, with "back" / "view result" affordances to move
 *    between the two steps.
 *
 * The inline CTA replaces the `SignUpPopover` on purpose: a popover is itself a
 * dialog, and stacking a dialog on top of the gallery's modal is the UX we want
 * to avoid here. Rate-limited / quota / video states keep their own CTA inside
 * `FlowTemplateFormCard`, so the signup block below only covers image success;
 * once a result exists the form's submit drops to `secondary` so signup is the
 * single primary CTA on screen.
 */
export function FlowTemplateRunPanel(props: FlowTemplateRunPanelProps) {
  return (
    <BlockErrorBoundary blockType='FlowTemplateRunPanel' fallback={null}>
      <FlowTemplateRunPanelInner {...props} />
    </BlockErrorBoundary>
  );
}

function FlowTemplateRunPanelInner({ flowTemplate, flowTemplateExample, resultType }: FlowTemplateRunPanelProps) {
  const t = useTranslations("ClientKeys.creative_templates");
  const tGallery = useTranslations("ClientKeys.flow_template_gallery");

  const { step, error, resultUrl, redirectCtaUrl, validateFile, handleFlowSubmit } = useFlowTemplateRun({
    flowTemplate,
    resultType,
    analyticsSource: ANALYTICS_SOURCE,
  });

  const hasResult = step === "result" && !!resultUrl;
  // Only image runs generate a result in this column; video uploads then route
  // the visitor to the app (step goes uploading → rate_limited, no in-page
  // result), so the "Generating your result…" spinner would over-promise there.
  const showRunning = isProcessingStep(step) && resultType === "image";

  // Mobile only: which step is on screen. Auto-advances to the result step when a
  // fresh result lands, but the "back" / "view result" controls still let the
  // user move between steps — deriving purely from `hasResult` would trap them on
  // the result. Adjusted during render keyed on the result URL (React's "adjust
  // state on change" pattern), so it re-advances on every new result, including
  // after a re-run. Ignored at lg+, where both columns show side by side.
  const [mobileShowResult, setMobileShowResult] = useState(false);
  const [seenResultUrl, setSeenResultUrl] = useState(resultUrl);
  if (resultUrl !== seenResultUrl) {
    setSeenResultUrl(resultUrl);
    setMobileShowResult(hasResult);
  }

  const signupFeatures = useSignUpFeatureItems();

  return (
    <div className='tw-flex tw-flex-col tw-gap-6 lg:tw-grid lg:tw-grid-cols-2 lg:tw-gap-8'>
      {/* Persistent SR-only status: announces the run on both mobile and desktop
          (the visible spinner lives in the result column, which is hidden on
          mobile). `role='status'` is an implicit polite live region — no
          `aria-busy`, which would otherwise defer the announcement. */}
      <p className='tw-sr-only' role='status'>
        {showRunning ? tGallery("result_running") : ""}
      </p>

      {/* Form column — always visible on desktop; hidden on mobile while the
          result step is showing. */}
      <div className={clsx("tw-flex tw-flex-col tw-gap-4 lg:tw-block", mobileShowResult && "tw-hidden")}>
        {/* Mobile-only forward nav: once a result exists, let the user step back
            to it after tapping "back" to tweak inputs. */}
        {hasResult && (
          <button
            type='button'
            onClick={() => setMobileShowResult(true)}
            className={clsx(STEP_NAV_CLASS, "tw-self-end")}
          >
            {tGallery("view_result")}
            <IconChevronRight className={clsx(iconStrokeFix, "tw-size-4")} />
          </button>
        )}
        <FlowTemplateFormCard
          flowTemplate={flowTemplate}
          flowTemplateExample={flowTemplateExample}
          step={step}
          error={error}
          validateFile={validateFile}
          onFlowSubmit={handleFlowSubmit}
          rateLimited={isRateLimitedStep(step)}
          isVideo={resultType === "video"}
          redirectCtaUrl={redirectCtaUrl}
          source={ANALYTICS_SOURCE}
          // Once a result exists the signup CTA in the result column is the primary
          // action, so demote "Generate" to avoid two competing primary buttons.
          submitVariant={hasResult ? "secondary" : "primary"}
        />
      </div>

      {/* Preview / result column — always visible on desktop (placeholder before
          a result); on mobile it only appears once there's a result. */}
      <div className={clsx("tw-flex-col tw-gap-4 lg:tw-flex", mobileShowResult ? "tw-flex" : "tw-hidden")}>
        {mobileShowResult && (
          <button
            type='button'
            onClick={() => setMobileShowResult(false)}
            className={clsx(STEP_NAV_CLASS, "tw-self-start")}
          >
            <IconChevronLeft className={clsx(iconStrokeFix, "tw-size-4")} />
            {tGallery("back_to_form")}
          </button>
        )}

        {hasResult && resultUrl ? (
          <>
            <FlowTemplateResultImage
              src={resultUrl}
              alt={flowTemplate.name ?? tGallery("result_image_alt")}
              className='tw-mx-auto tw-max-w-[55vh]'
            />
            {/* Inline signup block — mirrors the SignUpPopover's headline +
                value-prop bullets + CTA, kept in-page so we don't stack a dialog. */}
            <div className='tw-flex tw-flex-col tw-gap-5'>
              <p className='tw-type-2xl tw-font-display tw-font-light tw-text-black'>{t("signup_popover_title")}</p>
              <SignUpFeatureList items={signupFeatures} className='tw-text-black' />
              <LinkTracked
                asButton
                buttonVariants={{ variant: "primary", size: "lg" }}
                className='tw-h-[60px] tw-w-full tw-justify-center tw-rounded-full'
                href={redirectCtaUrl ?? publicUrls.signUp}
                eventName={EventName.mw_creative_template_omnibox_limit_sign_up_clicked}
                eventProperties={{ source: ANALYTICS_SOURCE }}
              >
                {t("signup_popover_cta")}
              </LinkTracked>
            </div>
          </>
        ) : showRunning ? (
          // Mirrors the form button's "Running" state in the preview pane so the
          // (desktop) result column reflects the in-flight run instead of the
          // idle hint. The SR announcement comes from the panel-root status region
          // above, so the placeholder itself is decorative (`aria-hidden`).
          <FlowTemplateRunningPlaceholder className='lg:tw-aspect-auto lg:tw-h-full' />
        ) : (
          // Desktop-only idle placeholder (the column is hidden on mobile until a
          // result exists) — keeps the two-column layout balanced before a run.
          <FlowTemplateIdlePlaceholder className='lg:tw-aspect-auto lg:tw-h-full' />
        )}
      </div>
    </div>
  );
}
